import { CalendarClock, CarFront, ChevronRight, MapPin, Star, TriangleAlert } from 'lucide-react';
import type {
  Conversation,
  CrmContact,
  DispatchBooking,
  Inbox,
  LatestContactBookings,
  SubjectBooking,
} from '../../../api/types';
import { ContactAvatar } from '../../../components/ContactAvatar';
import { contactIdentityLine } from '../../../lib/channelMeta';
import { useTimeFormat } from '../../../lib/tenantTime';
import type { LoadState } from '../../../lib/useLoad';
import { JobLink } from '../booking/JobLink';
import { lastReservationState, subjectNamed } from '../booking/lastReservation';
import { hasDispatchBooker } from './latestBookings';
import { bookingRef, bookingTone, pickupLabel } from '../bookingFormat';
import { DispatchRoleChip } from '../DispatchRoleChip';
import { contactDisplayName, primaryIdentity } from '../dispatchIdentity';

/**
 * Who this mail is from, and what they last booked — the strip under the
 * subject of the mail layout's reading view.
 *
 * The three-pane layout says the first half in its header and the second in the
 * contact panel beside the thread; a full-width reading view has room for
 * neither, and the drawer behind Profile is one click an agent answering mail
 * should not need to know who is writing. So this is the SUMMARY of both, and
 * "All bookings" opens the drawer for the rest.
 *
 * The reservation is the one the list chip shows, read the same way
 * (GET /contacts/latest_bookings, for this one contact): the booking most
 * recently MADE, which the panel's pickup-ordered list of six cannot answer.
 * It keeps the panel's rule about empties — a read that could not run, a
 * contact with no dispatch customer and a customer who has never booked are
 * three different sentences, never one blank.
 *
 * When the mail's SUBJECT names a reservation (`named`) the strip shows that
 * one instead and says so — "In the subject" — because a reply to a
 * confirmation is about the trip it confirmed, not the one booked since.
 */
export function MailContact({
  conversation,
  inbox,
  detail,
  latest,
  named = null,
  namedPending = false,
  onOpenProfile,
}: {
  conversation: Conversation;
  inbox: Inbox | undefined;
  detail: LoadState<CrmContact>;
  latest: LoadState<LatestContactBookings>;
  /** The reservation this thread's subject names, when it names one. */
  named?: SubjectBooking | null;
  /** The subject read is still out — hold the skeleton rather than flash the last reservation. */
  namedPending?: boolean;
  /** Open the contact drawer — never toggle it shut from here. */
  onOpenProfile: () => void;
}) {
  const contact = detail.data ?? conversation.contact;
  const customer = detail.data?.dispatchCustomer ?? null;
  // The ThreadPane header's rule: the record dispatch VERIFIED names them first.
  const name = customer?.name || contactDisplayName(contact);
  const identity = customer ? null : primaryIdentity(contact);
  const company = customer?.business?.name ?? null;
  const address = contactIdentityLine(contact, inbox?.channelType);
  const rides = customer?.rides ?? null;

  return (
    <div className="gm-who">
      <div className="gm-who-person">
        <ContactAvatar
          name={name}
          email={customer?.email ?? contact.email}
          photoUrl={detail.data?.avatarUrl}
          size={36}
        />
        <div className="gm-who-main">
          <div className="gm-who-line">
            <span className="gm-who-name" title={name}>
              {name}
            </span>
            {customer ? (
              <span
                className="cv-row-role"
                style={{ background: 'var(--c-role-customer-muted)', color: 'var(--c-role-customer)' }}
                title={`Dispatch customer #${customer.id}${contact.dispatchLinkVerified ? ' · verified' : ''}`}
              >
                {customer.customerType?.label ?? 'Customer'}
              </span>
            ) : (
              identity && <DispatchRoleChip role={identity.role} />
            )}
            {customer?.priority && Number(customer.priority.code) > 1 && (
              <span
                className="cv-row-role"
                style={{ background: 'var(--c-orange-muted)', color: 'var(--c-orange)' }}
                title="Dispatch customer priority"
              >
                <Star size={9} style={{ marginRight: 3 }} />
                {customer.priority.label}
              </span>
            )}
            {customer?.isActive === false && (
              <span className="cv-row-role" style={{ background: 'var(--c-red-muted)', color: 'var(--c-red)' }}>
                Inactive
              </span>
            )}
          </div>
          <div className="gm-who-sub">
            {[address, company, rides !== null ? `${rides} ride${rides === 1 ? '' : 's'}` : null]
              .filter(Boolean)
              .join(' · ') || 'No contact details yet'}
          </div>
        </div>
      </div>

      <div className="gm-who-trip">
        <div className="gm-who-label" title={named ? subjectNamed(named) : undefined}>
          {named ? 'In the subject' : 'Last reservation'}
          {/* The way to everything this strip leaves out. Only once there is a
              booking — otherwise the drawer would open on the same "not
              linked" or "no rides" this already says. */}
          {latest.data?.payload[0]?.booking && (
            <button type="button" className="gm-who-all" onClick={onOpenProfile}>
              All reservations
              <ChevronRight size={11} />
            </button>
          )}
        </div>
        {named ? (
          <ReservationLine booking={named.booking} />
        ) : namedPending ? (
          <div className="gm-who-trip-body" aria-busy>
            <span className="sk" style={{ display: 'block', height: 9, width: '70%', marginTop: 4 }} />
          </div>
        ) : (
          <LastReservation latest={latest} linked={hasDispatchBooker(conversation.contact)} />
        )}
      </div>
    </div>
  );
}

function LastReservation({
  latest,
  linked,
}: {
  latest: LoadState<LatestContactBookings>;
  /** What the conversation row says about the link, for the case the read is silent. */
  linked: boolean;
}) {
  // Named, never blank — the rules are lastReservationState's, shared with the
  // three-pane layout's strip so the two never disagree about an empty.
  const state = lastReservationState(latest, linked);
  if (state.kind === 'loading') {
    return (
      <div className="gm-who-trip-body" aria-busy>
        <span className="sk" style={{ display: 'block', height: 9, width: '70%', marginTop: 4 }} />
      </div>
    );
  }
  if (state.kind === 'warn') {
    return (
      <div className="gm-who-trip-body gm-who-warn">
        <TriangleAlert size={12} />
        <span>{state.text}</span>
      </div>
    );
  }
  if (state.kind === 'none') return <div className="gm-who-trip-body gm-who-none">{state.text}</div>;
  return <ReservationLine booking={state.booking} />;
}

function ReservationLine({ booking }: { booking: DispatchBooking }) {
  const { zone, formats } = useTimeFormat();
  const tone = bookingTone(booking.status.state);
  const route = [booking.pickup, booking.dropoff].filter(Boolean).join(' → ');
  return (
    <div className="gm-who-trip-body">
      <div className="gm-who-trip-top">
        <JobLink kind={booking.kind} id={booking.id} className="gm-who-ref" icon>
          {bookingRef(booking)}
        </JobLink>
        <span className="cv-row-role" style={{ background: tone.bg, color: tone.fg }}>
          {booking.status.label}
        </span>
        <span className="gm-who-when">
          <CalendarClock size={11} />
          {pickupLabel(booking.pickupAt, booking.timeZone ?? zone, formats)}
        </span>
      </div>
      {(route || booking.vehicle) && (
        <div className="gm-who-trip-route">
          {/* The text keeps a box of its own: a flex row cannot ellipsis a bare
              text node, and an airport address will not fit. */}
          {route && (
            <span className="gm-who-leg" title={route}>
              <MapPin size={11} />
              <span className="gm-who-clip">{route}</span>
            </span>
          )}
          {booking.vehicle && (
            <span className="gm-who-car" title={booking.vehicle}>
              <CarFront size={11} />
              <span className="gm-who-clip">{booking.vehicle}</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
